import { 
  Typography, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Divider 
} from '@mui/material';
import CodeBlock from '../../CodeBlock';

function HSCodesEndpoint() {
  return (
    <Box sx={{ textAlign: 'left', maxWidth: '900px' }}>
 
      <Typography variant="body1"   sx={{ my: 3 }}>
        The HS Codes Service allows you to search for Harmonized System (HS) codes by section.
        This is a convenience service provided alongside the EUDR API for easier integration.
        While this service is non-billable like the Echo service, it still requires an API key as it is metered.
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }} fontWeight="500">
        Base Endpoints
      </Typography>
      <CodeBlock code="GET /api/eudr/hscodes?section=IV" />
      <CodeBlock code="POST /api/eudr/hscodes" />

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }} fontWeight="500">
        Request Requirements 
      </Typography> 
      <Typography variant="subtitle1" gutterBottom>
        Mandatory Fields:
      </Typography>
      <ul>
        <li>Authorization header with valid API token</li>
      </ul>
      
      <Typography variant="subtitle1" gutterBottom>
        Optional Parameters:
      </Typography>
      <ul>
        <li>section - Filter HS codes by section (partial matching supported)</li>
      </ul>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }} fontWeight="500">
        Example Request Body (<small>POST</small>)
      </Typography>
      <CodeBlock 
        code={`{
  "section": "IV" 
}`} 
      />

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" gutterBottom sx={{ mb: 2 }} fontWeight="500">
        Response Handling
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Condition</TableCell>
              <TableCell>Resolution</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>200</TableCell>
              <TableCell>Success - HS Codes retrieved</TableCell>
              <TableCell>No action required</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>401</TableCell>
              <TableCell>Invalid security headers</TableCell>
              <TableCell>Verify API key and headers</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>500</TableCell>
              <TableCell>Server error</TableCell>
              <TableCell>Contact support</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }} fontWeight="500">
        Example Response
      </Typography>
      <CodeBlock 
        code={`{
  "status": "success",
  "data": [
      {
            "section": "IV",
            "name": "Prepared foodstuffs; beverages, spirits and vinegar; tobacco and manufactured tobacco substitutes",
            "codes": [
                {
                    "hscode": "16",
                    "name": "Meat, fish, crustaceans, molluscs or other aquatic invertebrates, or insects; preparations thereof",
                    "codes": [
                        {
                            "hscode": "1601",
                            "name": "Sausages and similar products, of meat, meat offal, blood or insects; food preparations based on these products",
                            "codes": [
                                {
                                    "hscode": "160100",
                                    "name": "Meat preparations; sausages and similar products, of meat, meat offal, blood or insects, and food preparations based on these products"
                                }
                            ]
                        },
                        {
                            "hscode": "1602",
                            "name": "Prepared or preserved meat, meat offal, blood or insects",
                            "codes": [
                                {
                                    "hscode": "160210",
                                    "name": "Meat preparations; homogenised preparations of meat, meat offal blood or insects"
                                },
                                {
                                    "hscode": "160220",
                                    "name": "Meat preparations; of the prepared or preserved liver of any animal (excluding homogenised preparations)"
                                },
                                ...
                            ]
                        },
                        {
                            "hscode": "1603",
                            "name": "Extracts and juices of meat, fish or crustaceans, molluscs or other aquatic invertebrates",
                            "codes": [
                                {
                                    "hscode": "160300",
                                    "name": "Extracts and juices; of meat, fish or crustaceans, molluscs or other aquatic invertebrates"
                                }
                            ]
                        },
                        ...                        
                    ]
                }
            ]
        }
    ]
}`} 
      />

      <Typography variant="body1" paragraph sx={{ mt: 3, fontStyle: 'italic' }}>
        Note: This service is provided for convenience and is not directly part of the core EUDR services.
        The response structure may vary based on the available HS codes in the database.
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }} fontWeight="500">
        Authentication
      </Typography>
      <CodeBlock code="Authorization: Bearer your-api-key-here" />
      <Typography variant="body1" paragraph sx={{ mt: 2 }}>
        While this is a non-billable operation, an API key is still required for metering purposes.
      </Typography>
    </Box>
  );
}

export default HSCodesEndpoint; 